/**
 * What the breakdown card shows for a month with nothing in it.
 *
 * A greyed-out mock of the chart rather than a blank space — the shape tells the user what
 * will appear here once there is something to draw, which is how the app the PO uses handles
 * the same case. Drawn as inline SVG, not ECharts: there is no data to plot, so loading a
 * chart engine to render a placeholder would be absurd.
 */
import { Link } from 'react-router';

import styles from './EmptyBreakdown.module.css';

/**
 * Ring geometry, derived rather than hand-written.
 *
 * The first version hard-coded r=70 with a 26-wide stroke inside a 160-unit box: the stroke
 * spans r ± 13, so its outer edge landed at 83 while the box only reaches 80 from the centre.
 * The browser clipped three units off all four sides and the ring came out visibly flattened —
 * the distortion the PO reported. Every number below is computed from the box so that cannot
 * happen again: the outer edge is defined as half the height minus the padding.
 *
 * The box is wider than it is tall because the labels sit outside the ring, as they do on the
 * real chart and in the reference the PO supplied.
 */
const WIDTH = 240;
const HEIGHT = 172;
const CENTRE_X = WIDTH / 2;
const CENTRE_Y = HEIGHT / 2;
const STROKE = 20;
// Room outside the ring for a leader line plus half a pill, with a little to spare.
const PADDING = 26;
const RADIUS = CENTRE_Y - PADDING - STROKE / 2;

/**
 * Three equal segments with an even gap between them.
 *
 * The dash pattern is one third of the circumference, so it tiles the circle exactly three
 * times whatever the radius — there is no leftover to make the last gap a different width from
 * the first two.
 */
const SEGMENTS = 3;
const GAP_RATIO = 0.11;
const PERIOD = (2 * Math.PI * RADIUS) / SEGMENTS;
const DASH = PERIOD * (1 - GAP_RATIO);
const GAP = PERIOD * GAP_RATIO;

const LABEL_WIDTH = 42;
const LABEL_HEIGHT = 18;
/** Where the leader line ends and the pill begins, measured from the centre. */
const LABEL_RADIUS = RADIUS + STROKE / 2 + 12;

/**
 * One placeholder label per segment, sitting off the middle of its arc.
 *
 * Angles are measured clockwise from twelve o'clock, matching the `rotate(-90)` the ring is
 * drawn with, so a segment's midpoint is half a dash past where its period starts.
 */
const LABELS = Array.from({ length: SEGMENTS }, (_, index) => {
  const midpointFraction = (index + (1 - GAP_RATIO) / 2) / SEGMENTS;
  const angle = midpointFraction * 2 * Math.PI;
  const x = CENTRE_X + Math.sin(angle) * LABEL_RADIUS;
  const y = CENTRE_Y - Math.cos(angle) * LABEL_RADIUS;
  // Pills on the left of the ring hang to the left of their leader line, and vice versa, so
  // none of them can reach past the edge of the box.
  const towardsLeft = x < CENTRE_X;

  return {
    lineFrom: {
      x: CENTRE_X + Math.sin(angle) * (RADIUS + STROKE / 2 + 2),
      y: CENTRE_Y - Math.cos(angle) * (RADIUS + STROKE / 2 + 2),
    },
    lineTo: { x, y },
    pill: {
      x: towardsLeft ? x - LABEL_WIDTH : x,
      y: y - LABEL_HEIGHT / 2,
    },
    text: { x: towardsLeft ? x - LABEL_WIDTH / 2 : x + LABEL_WIDTH / 2, y },
  };
});

export function EmptyBreakdown({
  message,
  actionLabel,
  actionHref,
}: {
  message: string;
  /** e.g. "Nhập giao dịch chi" — the way out of the empty state. */
  actionLabel: string;
  /** Quick Add, opened on the tab this button promises. */
  actionHref: string;
}) {
  return (
    <div className={styles.empty}>
      <p className={styles.message}>{message}</p>
      <Link className={styles.action} to={actionHref}>
        <span aria-hidden="true">+</span> {actionLabel}
      </Link>

      <svg
        className={styles.mock}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Chưa có dữ liệu để vẽ biểu đồ"
      >
        <circle
          className={styles.ring}
          cx={CENTRE_X}
          cy={CENTRE_Y}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          strokeDasharray={`${DASH} ${GAP}`}
          // The first segment starts at twelve o'clock, where the real chart's biggest slice
          // begins, so the placeholder and the thing it stands in for are the same shape.
          transform={`rotate(-90 ${CENTRE_X} ${CENTRE_Y})`}
        />

        {LABELS.map((label, index) => (
          <g key={index}>
            <line
              className={styles.leader}
              x1={label.lineFrom.x}
              y1={label.lineFrom.y}
              x2={label.lineTo.x}
              y2={label.lineTo.y}
            />
            <rect
              className={styles.pill}
              x={label.pill.x}
              y={label.pill.y}
              width={LABEL_WIDTH}
              height={LABEL_HEIGHT}
              rx={LABEL_HEIGHT / 2}
            />
            <text
              className={styles.pillText}
              x={label.text.x}
              y={label.text.y}
              textAnchor="middle"
              dominantBaseline="central"
            >
              X%
            </text>
          </g>
        ))}
      </svg>

      <ul className={styles.mockRows} aria-hidden="true">
        {[0, 1].map((row) => (
          <li key={row} className={styles.mockRow}>
            <span className={styles.mockSwatch} />
            <span className={styles.mockName} />
            <span className={styles.mockAmount} />
          </li>
        ))}
      </ul>
    </div>
  );
}
